import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Wallet, ArrowRight, Mail, Lock, User as UserIcon, Sparkles, Mic, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      toast.success(mode === "login" ? "Bem-vindo de volta!" : "Conta criada com sucesso!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível continuar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row relative">
      {/* -------- Left / Hero brand -------- */}
      <aside className="relative flex-1 pnc-hero-glow overflow-hidden lg:min-h-screen flex items-center justify-center px-6 sm:px-10 pt-14 pb-10 lg:pt-0 lg:pb-0">
        <div className="relative z-10 w-full max-w-lg animate-slide-up">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-2xl shadow-emerald-500/40 relative">
              <Wallet className="w-7 h-7 text-slate-900" strokeWidth={2.5} />
              <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-sky-400 border-2 border-slate-950 flex items-center justify-center">
                <Mic className="w-2.5 h-2.5 text-slate-900" strokeWidth={3} />
              </span>
            </div>
            <div>
              <p className="font-display text-2xl font-black tracking-tight text-white">Põe na Conta</p>
              <p className="text-xs text-emerald-300/80 font-medium">Finanças por voz</p>
            </div>
          </div>

          {/* Headline */}
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.24em] text-emerald-300 font-semibold mb-4 flex items-center gap-2">
            <span className="w-6 h-px bg-emerald-400" /> Bem-vindo
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight">
            Sua vida financeira,
            <br />
            <span className="bg-gradient-to-r from-emerald-300 via-emerald-400 to-sky-300 bg-clip-text text-transparent">
              na ponta da língua.
            </span>
          </h1>
          <p className="mt-5 text-slate-300 text-sm sm:text-base max-w-md leading-relaxed">
            Organize suas despesas e receitas com a praticidade da sua voz. Fale naturalmente, revise em segundos e mantenha o controle no bolso.
          </p>

          {/* Value bullets */}
          <div className="mt-8 grid grid-cols-1 gap-3 max-w-md">
            {[
              { icon: Mic, title: "Registre falando", text: "“Gasolina 50 reais e café 15”." },
              { icon: Sparkles, title: "Categorize sozinho", text: "IA identifica valor, tipo e categoria." },
              { icon: ShieldCheck, title: "Seus dados, seus", text: "Cada usuário vê só a própria conta." },
            ].map((b) => (
              <div key={b.title} className="flex items-start gap-3 p-3 rounded-2xl bg-slate-900/40 border border-white/5">
                <div className="w-9 h-9 shrink-0 rounded-xl bg-emerald-500/15 text-emerald-300 flex items-center justify-center">
                  <b.icon className="w-4 h-4" strokeWidth={2.3} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{b.title}</p>
                  <p className="text-xs text-slate-400 truncate">{b.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </aside>

      {/* -------- Right / Auth form -------- */}
      <section className="relative w-full lg:w-[520px] flex items-center justify-center px-5 sm:px-8 pb-16 lg:pb-0 lg:min-h-screen">
        <div className="w-full max-w-md animate-slide-up">
          <div className="pnc-card-solid p-6 sm:p-8 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />

            <div className="relative">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-display text-2xl font-bold text-white">
                    {mode === "login" ? "Entrar" : "Criar conta"}
                  </h2>
                  <p className="text-xs text-slate-400 mt-1">
                    {mode === "login" ? "Acesse seu painel financeiro" : "Comece a organizar suas finanças hoje"}
                  </p>
                </div>
              </div>

              {/* Tab pill */}
              <div className="flex gap-1 mb-6 p-1 rounded-full bg-slate-900/60 border border-white/5">
                <button
                  data-testid="auth-tab-login"
                  onClick={() => setMode("login")}
                  className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${
                    mode === "login"
                      ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-slate-900 shadow-lg shadow-emerald-500/25"
                      : "text-slate-400 hover:text-white"
                  }`}
                >Entrar</button>
                <button
                  data-testid="auth-tab-register"
                  onClick={() => setMode("register")}
                  className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${
                    mode === "register"
                      ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-slate-900 shadow-lg shadow-emerald-500/25"
                      : "text-slate-400 hover:text-white"
                  }`}
                >Criar conta</button>
              </div>

              <form onSubmit={submit} className="space-y-4">
                {mode === "register" && (
                  <Field icon={UserIcon} label="Nome completo">
                    <input
                      data-testid="auth-input-name"
                      type="text" required
                      value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="Seu nome"
                      className="w-full bg-transparent outline-none text-white placeholder:text-slate-500 text-sm"
                    />
                  </Field>
                )}

                <Field icon={Mail} label="E-mail">
                  <input
                    data-testid="auth-input-email"
                    type="email" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="voce@exemplo.com"
                    className="w-full bg-transparent outline-none text-white placeholder:text-slate-500 text-sm"
                  />
                </Field>

                <Field icon={Lock} label="Senha">
                  <input
                    data-testid="auth-input-password"
                    type={showPw ? "text" : "password"} required minLength={6}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="w-full bg-transparent outline-none text-white placeholder:text-slate-500 text-sm"
                  />
                  <button
                    type="button"
                    aria-label={showPw ? "Ocultar senha" : "Mostrar senha"}
                    onClick={() => setShowPw((v) => !v)}
                    className="text-slate-400 hover:text-white p-1"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </Field>

                <button
                  data-testid="auth-submit-btn"
                  type="submit" disabled={loading}
                  className="btn-emerald w-full py-3.5 rounded-2xl flex items-center justify-center gap-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Aguarde…" : (mode === "login" ? "Entrar na conta" : "Criar minha conta")}
                  <ArrowRight className="w-4 h-4" />
                </button>

                {mode === "login" && (
                  <div className="text-center pt-1">
                    <Link
                      data-testid="login-forgot-password-link"
                      to="/esqueci-senha"
                      className="text-xs text-slate-400 hover:text-emerald-300 underline underline-offset-4"
                    >
                      Esqueci minha senha
                    </Link>
                  </div>
                )}
              </form>

              <div className="pnc-divider my-6" />

              <p className="text-[11px] text-slate-500 text-center leading-relaxed">
                Ao continuar você concorda com nossa política de privacidade.
                <br />Seus dados são criptografados e nunca compartilhados.
              </p>
            </div>
          </div>

          <p className="text-center text-xs text-slate-500 mt-6">
            © 2026 Põe na Conta • Feito com <span className="text-emerald-400">voz</span> no Brasil
          </p>
        </div>
      </section>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
      <div className="mt-1.5 flex items-center gap-2.5 px-4 py-3 rounded-2xl bg-slate-900/70 border border-white/10 focus-within:border-emerald-400/60 focus-within:bg-slate-900 transition-colors">
        <Icon className="w-4 h-4 text-slate-500 shrink-0" />
        {children}
      </div>
    </label>
  );
}
